import { Dropdown, DropdownProps } from '@components/Dropdown'
import React from 'react'
import {
	Button,
	ButtonGroup,
	FormGroup,
	FormInput,
	FormLabel,
	FormTextarea,
} from './FormComponents.styles'

interface FormGroupProps {
	label: string
	htmlFor?: string
	children: React.ReactNode
}

export const FormGroupComponent: React.FC<FormGroupProps> = ({ label, htmlFor, children }) => (
	<FormGroup>
		<FormLabel htmlFor={htmlFor}>{label}</FormLabel>
		{children}
	</FormGroup>
)

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label: string
	htmlFor?: string
	$error?: boolean
}

export const FormInputComponent: React.FC<FormInputProps> = ({ label, htmlFor, $error, ...props }) => (
	<FormGroupComponent label={label} htmlFor={htmlFor}>
		<FormInput $error={$error} {...props} />
	</FormGroupComponent>
)

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	label: string
	htmlFor?: string
}

export const FormTextareaComponent: React.FC<FormTextareaProps> = ({ label, htmlFor, ...props }) => (
	<FormGroupComponent label={label} htmlFor={htmlFor}>
		<FormTextarea {...props} />
	</FormGroupComponent>
)

type FormSelectProps<T extends string> = Omit<DropdownProps<T>, 'aria-label'> & {
	label: string
	htmlFor?: string
}

/** A labelled single-choice field on the app's Dropdown; the label names it for assistive tech too. */
export function FormSelectComponent<T extends string>({ label, htmlFor, ...props }: FormSelectProps<T>) {
	return (
		<FormGroupComponent label={label} htmlFor={htmlFor}>
			<Dropdown aria-label={label} {...props} />
		</FormGroupComponent>
	)
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'primary' | 'secondary' | 'danger'
	children: React.ReactNode
}

export const ButtonComponent: React.FC<ButtonProps> = ({ variant = 'secondary', children, ...props }) => (
	<Button variant={variant} {...props}>
		{children}
	</Button>
)

interface ButtonGroupProps {
	children: React.ReactNode
	align?: 'left' | 'center' | 'right'
}

export const ButtonGroupComponent: React.FC<ButtonGroupProps> = ({ children, align = 'right' }) => (
	<ButtonGroup $align={align}>{children}</ButtonGroup>
)
